import type { SharedViewFailureDiagnosisKind } from '@/utils/filter/classifySharedViewFailure';
import type { SharedViewFailureStage } from '@/utils/filter/sharedViewDiagnostics';
import type { AnalyticsPayload } from '@/hooks/AdobeAnalyticsHooks/types/AdobeAnalyticsTypes';
import { AdobeAnalyticsConst } from './adobeAnalyticsConstants/adobeAnalyticsConstants';

export type SharedViewVerifySource = 'shareButton' | 'manageSavedFilters';

export interface SharedViewLoadFailedDetails {
  diagnosis: SharedViewFailureDiagnosisKind;
  stage: SharedViewFailureStage;
  httpStatus?: number;
  errorCode?: string;
  errorName?: string;
}

export interface SharedViewVerifyFailedDetails {
  source: SharedViewVerifySource;
}

type SharedViewAnalyticsDetails = SharedViewLoadFailedDetails | SharedViewVerifyFailedDetails;

const buildPayload = (event: string, details: SharedViewAnalyticsDetails): AnalyticsPayload => ({
  event,
  timestamp: Date.now(),
  page: {
    siteName: AdobeAnalyticsConst.SITE_DETAILS.name,
    language: window.navigator?.language || 'en',
    domain: window.location?.hostname ?? '',
  },
  details,
});

export const getSharedViewLoadFailedPayload = ({
  diagnosis,
  stage,
  httpStatus,
  errorCode,
  errorName,
}: SharedViewLoadFailedDetails): AnalyticsPayload =>
  buildPayload(AdobeAnalyticsConst.EVENTS.SHARED_VIEW_LOAD_FAILED, {
    diagnosis,
    stage,
    httpStatus,
    errorCode,
    errorName,
  });

export const getSharedViewVerifyFailedPayload = (source: SharedViewVerifySource): AnalyticsPayload =>
  buildPayload(AdobeAnalyticsConst.EVENTS.SHARED_VIEW_VERIFY_FAILED, { source });

const pushToDataLayer = (payload: AnalyticsPayload): void => {
  try {
    window.oneDataLayer = window.oneDataLayer || [];
    window.oneDataLayer.push(payload);
  } catch {
    // Analytics must never break the share flow
  }
};

export const trackSharedViewLoadFailed = (details: SharedViewLoadFailedDetails): void =>
  pushToDataLayer(getSharedViewLoadFailedPayload(details));

export const trackSharedViewVerifyFailed = (source: SharedViewVerifySource): void =>
  pushToDataLayer(getSharedViewVerifyFailedPayload(source));
